import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../contexts/ToastContext";
import { api } from "../lib/api";
import { currencySymbol, formatMoney } from "../lib/format";
import { BalancesOut, MeOut, TransferOut } from "../lib/schemas";
import { webApp, useBackButton, useMainButton } from "../lib/telegram";
import { MoneyInput, Skeleton } from "../ui";

type Tab = "pay" | "all";

export default function Settle() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const nav = useNavigate();
  const showToast = useToast();

  const [balancesData, setBalancesData] = useState<BalancesOut | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>("pay");
  const [selectedTransferIndex, setSelectedTransferIndex] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [autoFilledAmount, setAutoFilledAmount] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [planExpanded, setPlanExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [data, me] = await Promise.all([
          api<BalancesOut>("GET", `/chats/${groupId}/balances`),
          api<MeOut>("GET", "/me"),
        ]);
        if (cancelled) return;
        setBalancesData(data);
        setCurrentUserId(me.user.id);
      } catch (e) {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : "Failed to load");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [groupId]);

  const myDebts: TransferOut[] =
    balancesData && currentUserId !== null
      ? balancesData.plan.filter((t) => t.from_user.id === currentUserId)
      : [];

  const settlementPlan: TransferOut[] = balancesData?.plan ?? [];

  useEffect(() => {
    if (myDebts.length > 0 && selectedTransferIndex === null) {
      setSelectedTransferIndex(0);
      const a = myDebts[0].amount;
      setAmount(a);
      setAutoFilledAmount(a);
    }
  }, [myDebts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSelectTransfer(index: number) {
    setSelectedTransferIndex(index);
    const notEdited =
      amount === "" ||
      (autoFilledAmount !== null &&
        parseFloat(amount) === parseFloat(autoFilledAmount));
    if (notEdited) {
      const a = myDebts[index].amount;
      setAmount(a);
      setAutoFilledAmount(a);
    }
  }

  const submitPay = useCallback(async () => {
    if (submitting || selectedTransferIndex === null) return;
    const transfer = myDebts[selectedTransferIndex];
    if (!transfer) return;
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) return;
    setSubmitting(true);
    webApp.MainButton.showProgress(false);
    try {
      await api("POST", `/chats/${groupId}/settle`, {
        json: {
          target_user_id: transfer.to_user.id,
          amount: amountNum,
        },
      });
      showToast("Sent for approval", { type: "success" });
      nav(`/groups/${groupId}`, { replace: true });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Something went wrong", { type: "error" });
    } finally {
      setSubmitting(false);
      webApp.MainButton.hideProgress();
    }
  }, [submitting, selectedTransferIndex, myDebts, amount, groupId, showToast, nav]);

  const submitAll = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    webApp.MainButton.showProgress(false);
    try {
      await api("POST", `/chats/${groupId}/settle-all`, {});
      showToast("Sent for approval", { type: "success" });
      nav(`/groups/${groupId}`, { replace: true });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Something went wrong", { type: "error" });
    } finally {
      setSubmitting(false);
      webApp.MainButton.hideProgress();
    }
  }, [submitting, groupId, showToast, nav]);

  const handleBack = useCallback(() => nav(`/groups/${groupId}`), [nav, groupId]);
  useBackButton(handleBack);

  const amountNum = parseFloat(amount);
  const payReady =
    tab === "pay" &&
    selectedTransferIndex !== null &&
    !isNaN(amountNum) &&
    amountNum > 0 &&
    !submitting;

  const allReady = tab === "all" && settlementPlan.length > 0 && !submitting;
  const mainBtnEnabled = tab === "pay" ? payReady : allReady;
  const mainBtnVisible = tab === "pay" ? myDebts.length > 0 : settlementPlan.length > 0;

  const handleMainButton = useCallback(() => {
    if (tab === "pay") void submitPay();
    else void submitAll();
  }, [tab, submitPay, submitAll]);

  useMainButton("Send for approval", mainBtnEnabled && mainBtnVisible, handleMainButton);

  if (loadError) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-danger mb-4">{loadError}</p>
          <button
            type="button"
            onClick={() => nav(`/groups/${groupId}`)}
            className="border border-fg px-5 py-2.5 rounded-xl text-fg font-sans text-sm font-semibold active:opacity-70 transition-opacity"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (!balancesData) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <Skeleton className="h-7 w-28 mx-auto mb-6" />
        <Skeleton className="h-10 w-full mb-6" />
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-32">
      <h1 className="font-sans text-xl font-bold text-fg mb-6 text-center">
        Settle up
      </h1>

      {/* Segmented control */}
      <div className="flex gap-1 p-1 bg-surface border border-hairline rounded-lg mb-6">
        <button
          type="button"
          onClick={() => setTab("pay")}
          className={`flex-1 py-2.5 rounded-lg font-sans text-sm font-semibold transition-colors ${
            tab === "pay"
              ? "bg-fg text-bg"
              : "text-muted"
          }`}
        >
          Pay someone
        </button>
        <button
          type="button"
          onClick={() => setTab("all")}
          className={`flex-1 py-2.5 rounded-lg font-sans text-sm font-semibold transition-colors ${
            tab === "all"
              ? "bg-fg text-bg"
              : "text-muted"
          }`}
        >
          Settle all
        </button>
      </div>

      {/* Pay someone tab */}
      {tab === "pay" && (
        <div>
          {myDebts.length === 0 ? (
            <p className="text-muted text-sm text-center py-8">
              You don't owe anyone right now.
            </p>
          ) : (
            <>
              <p className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
                Who to pay
              </p>
              <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden mb-5">
                {myDebts.map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSelectTransfer(i)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0 transition-colors ${
                      selectedTransferIndex === i ? "bg-surface-2" : "active:bg-surface-2"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedTransferIndex === i
                            ? "border-fg bg-fg"
                            : "border-faint"
                        }`}
                      >
                        {selectedTransferIndex === i && (
                          <div className="w-1.5 h-1.5 rounded-full bg-bg" />
                        )}
                      </div>
                      <span className="text-fg">{t.to_user.first_name}</span>
                    </div>
                    <span className="font-mono [font-variant-numeric:tabular-nums] text-muted text-sm">
                      {formatMoney(t.amount, balancesData.currency)}
                    </span>
                  </button>
                ))}
              </div>

              <div className="mb-4">
                <MoneyInput
                  label="Amount"
                  currencySymbol={currencySymbol(balancesData.currency)}
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="text-lg"
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Settle all tab */}
      {tab === "all" && (
        <div>
          {settlementPlan.length === 0 ? (
            <p className="text-muted text-sm text-center py-8">
              All settled up ✓
            </p>
          ) : (
            <p className="text-muted text-sm text-center py-8">
              This will create settlement requests for the full plan below.
            </p>
          )}
        </div>
      )}

      {/* Settlement plan disclosure */}
      {settlementPlan.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setPlanExpanded(v => !v)}
            className="w-full flex items-center justify-between font-sans text-xs font-medium text-muted active:opacity-70 transition-opacity py-2 mb-2.5 px-1"
          >
            <span>Full settlement plan ({settlementPlan.length} transfer{settlementPlan.length !== 1 ? "s" : ""})</span>
            <span>{planExpanded ? "▲" : "▼"}</span>
          </button>
          {planExpanded && (
            <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
              {settlementPlan.map((t, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0">
                  <span className="text-fg text-sm">
                    {t.from_user.first_name}
                    <span className="text-muted mx-2">→</span>
                    {t.to_user.first_name}
                  </span>
                  <span className="font-mono text-sm [font-variant-numeric:tabular-nums] text-fg font-medium">
                    {formatMoney(t.amount, balancesData.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
